<template>
  <div v-if="banner" :class="['border-b px-4 py-2 text-sm', banner.className]">
    <div class="mx-auto flex max-w-7xl items-center justify-between gap-3">
      <p>{{ banner.message }}</p>
      <router-link
        v-if="banner.showBilling"
        class="rounded border px-2 py-1 text-xs font-semibold"
        :class="banner.linkClass"
        to="/payments/billing"
      >
        Manage billing
      </router-link>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { isAuthenticated } from '@/utils/auth'
import useEntitlements from '@/composables/useEntitlements'

const entitlementStore = useEntitlements()

const banner = computed(() => {
  if (!isAuthenticated()) return null
  const entitlements = entitlementStore.entitlements.value
  if (!entitlements) return null
  const mode = String(entitlements.access_mode || '').toLowerCase()
  const days = entitlements.days_until_period_end
  if (mode === 'limited') {
    const suffix = typeof days === 'number' ? ` Access remains active for ${Math.max(days, 0)} day(s).` : ''
    return {
      className: 'border-amber-200 bg-amber-50 text-amber-900',
      linkClass: 'border-amber-300 text-amber-900',
      message: `Your subscription is set to end at period close.${suffix}`,
      showBilling: true,
    }
  }
  if (mode === 'grace') {
    return {
      className: 'border-orange-300 bg-orange-50 text-orange-900',
      linkClass: 'border-orange-300 text-orange-900',
      message: 'Payment issue detected. Access is temporarily in grace mode; update billing to avoid restrictions.',
      showBilling: true,
    }
  }
  if (mode === 'admin_only') {
    const role = String(entitlements.membership_role || '').toLowerCase()
    const isAdmin = role === 'org_owner' || role === 'org_admin'
    return {
      className: 'border-red-300 bg-red-50 text-red-900',
      linkClass: 'border-red-300 text-red-900',
      message: isAdmin
        ? 'Organization is in admin-only recovery mode. Resolve billing to restore member access.'
        : 'Organization is currently in admin-only recovery mode. Contact your org admin for access restoration.',
      showBilling: isAdmin,
    }
  }
  if (mode === 'blocked') {
    return {
      className: 'border-red-400 bg-red-100 text-red-900',
      linkClass: 'border-red-400 text-red-900',
      message: 'Subscription access is blocked. Use billing recovery options to restore service.',
      showBilling: true,
    }
  }
  return null
})
</script>
